<template>
  <div class="fts-activity-portal-row">
    <div class="dm-ledger-rule">
      <div class="dm-ledger-rule-label" id="fts-activity-portal-label">Open with</div>
      <div class="dm-ledger-states" role="radiogroup" aria-labelledby="fts-activity-portal-label">
        <span
          class="dm-ledger-state"
          role="radio"
          tabindex="0"
          :aria-checked="String(!usesPortal)"
          :class="{ 'dm-ledger-state--on': !usesPortal }"
          @click="chooseStandard"
          @keydown.enter.prevent="chooseStandard"
          @keydown.space.prevent="chooseStandard"
        >Standard form</span>
        <span class="dm-ledger-sep">·</span>
        <span
          class="dm-ledger-state"
          role="radio"
          :tabindex="canPickPortal || usesPortal ? 0 : -1"
          :aria-checked="String(usesPortal)"
          :aria-disabled="String(!canPickPortal && !usesPortal)"
          :class="{ 'dm-ledger-state--on': usesPortal, 'activity-portal-state--locked': !canPickPortal && !usesPortal }"
          @click="choosePortalPage"
          @keydown.enter.prevent="choosePortalPage"
          @keydown.space.prevent="choosePortalPage"
        >Portal page</span>
      </div>
    </div>
    <div class="activity-portal-pick" v-if="usesPortal">
      <label class="dm-ledger-rule-label" for="fts-activity-portal-select">Portal</label>
      <select
        id="fts-activity-portal-select"
        class="activity-portal-select"
        :value="formData.portalId"
        :disabled="loading"
        @change="choosePortal($event.target.value)"
      >
        <option v-for="option in options" :key="option.id" :value="option.id">{{ option.label }}</option>
      </select>
    </div>
    <div class="activity-portal-note" v-if="note">{{ note }}</div>
  </div>
</template>

<script>
/**
 * "Open with" for Create and custom activities (docs/integration-portal-claude-design-proposal.md
 * §3.6, §3.8 in the SVN repo): the standard form, or one of this workspace's portals. Saved on the
 * activity as `portalId`; never together with `skipConfirm`, since the portal page is the screen
 * the member confirms on. Members then get the portal page in a new tab (activityPortal.js).
 *
 * The portals come from GET api/portal, the same list as Settings › Portals (workspace admins).
 * A hand-over's Studio link (portalSetupLink.js) sets it to the portal at the link's address, for the designer to
 * check and Save.
 */
import api from "../portals/portalApi";
import { errorMessage } from "../portals/portalFormat";
import { openWithOf, portalAt } from "../portals/portalSetupLink";

/** "AccountOps", "AccountOps (switched off)", "AccountOps (app only)". */
function optionLabel(p) {
  const name = String(p.name || "").trim() || "Portal";
  if (!p.enabled) return name + " (switched off)";
  if (!p.showOnWeb && p.showOnApp) return name + " (app only)";
  if (p.showOnWeb && !p.showOnApp) return name + " (web only)";
  return name;
}

export default {
  name: "FTSActivityPortalRow",
  props: {
    // the activity being designed: portalId and skipConfirm are written on it
    formData: { type: Object, required: true },
  },
  data: () => ({
    loading: true,
    portals: [],
    refusal: "",
    linkNote: "",
  }),
  computed: {
    usesPortal() {
      return Boolean(this.formData.portalId);
    },
    options() {
      const list = this.portals
        .filter((p) => p && p.id)
        .slice()
        .sort((a, b) => (Number(a.sequence) || 0) - (Number(b.sequence) || 0))
        .map((p) => ({ id: String(p.id), label: optionLabel(p), opens: Boolean(p.enabled && p.showOnWeb) }));
      const current = this.formData.portalId ? String(this.formData.portalId) : "";
      if (current && !list.some((o) => o.id === current)) {
        // Saved before, and no longer in the list (deleted, or the list could not be read): kept visible.
        const label = this.loading ? "Loading…" : "A portal this workspace no longer has";
        list.unshift({ id: current, label, opens: false });
      }
      return list;
    },
    canPickPortal() {
      return !this.loading && !this.refusal && this.portals.length > 0;
    },
    note() {
      if (this.refusal) return this.refusal;
      if (this.loading) return "";
      if (this.linkNote) return this.linkNote;
      if (this.usesPortal) {
        return "Members get this portal page in a new tab instead of the form. The page submits this same "
          + "activity, with its rights and rules. Skip confirmation does not apply.";
      }
      if (!this.portals.length) return "Add a portal in Settings › Portals to open this activity with a page of its own.";
      return "";
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.refusal = "";
      try {
        const res = await api.listPortals();
        const data = (res && res.data) || {};
        this.portals = Array.isArray(data.portals) ? data.portals : [];
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.portals = [];
        this.refusal = status === 404
          ? "Portal links are not switched on for this server."
          : errorMessage(err, "This workspace's portals could not be read.");
      } finally {
        this.loading = false;
      }
    },
    choosePortal(id) {
      if (!id) {
        this.chooseStandard();
        return;
      }
      this.linkNote = "";
      this.$set(this.formData, "portalId", String(id));
      // Not both: the portal page is where the member confirms (§3.6).
      if ("skipConfirm" in this.formData) this.$delete(this.formData, "skipConfirm");
    },
    chooseStandard() {
      this.linkNote = "";
      if ("portalId" in this.formData) this.$delete(this.formData, "portalId");
    },
    choosePortalPage() {
      if (this.usesPortal || !this.canPickPortal) return;
      // The first one members can open on the web, else the first one.
      const first = this.options.find((o) => o.opens) || this.options[0];
      if (first) this.choosePortal(first.id);
    },
    /** A hand-over's link for this activity: Open with set to the portal at its address. Read once. */
    applyLink() {
      const route = this.$route;
      const address = openWithOf(route && route.query, this.formData.id);
      if (!address || this.refusal) return;
      const query = { ...route.query };
      delete query.openWith;
      if (this.$router) this.$router.replace({ query }).catch(() => {});
      const portal = portalAt(this.portals, address);
      if (portal) this.choosePortal(portal.id);
      else this.linkNote = "This workspace has no portal at " + address + " yet. Add it in Settings › Portals, then open the link again.";
    },
  },
  created() {
    this.load().then(() => this.applyLink());
  },
};
</script>

<style lang="scss">
.fts-activity-portal-row {
  .activity-portal-state--locked {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .activity-portal-pick {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0 0 0.625rem;
  }

  .activity-portal-select {
    margin-left: auto;
    min-width: 0;
    max-width: 60%;
    height: 1.75rem;
    padding: 0 0.5rem;
    border: 1px solid var(--fts-border);
    border-radius: 0.375rem;
    background: var(--fts-card-background);
    color: var(--fts-text);
    font: inherit;
    font-size: 0.75rem;
  }

  .activity-portal-note {
    padding: 0 0 0.625rem;
    font-size: 0.6875rem;
    line-height: 1rem;
    color: var(--fts-form-helper);
  }
}
</style>
